/**
 * AdminDateFilter — shared date-range picker for admin panels.
 *
 * Renders two <input type="date"> fields + a Clear button. Change is
 * announced only when the user hits Apply (or clears) so we don't fire
 * a network call on every keystroke of a date picker.
 *
 * Usage:
 *   const [range, setRange] = useState({ from: '', to: '' });
 *   <AdminDateFilter value={range} onChange={setRange} />
 * Then include ``date_from`` / ``date_to`` in your API params from ``range``.
 */
import React, { useEffect, useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { Button } from '../ui/button';

export function AdminDateFilter({ value, onChange, testId = 'admin-date-filter', className = '' }) {
  const [from, setFrom] = useState(value?.from || '');
  const [to, setTo] = useState(value?.to || '');

  // Keep local state in sync when the parent resets externally.
  useEffect(() => {
    setFrom(value?.from || '');
    setTo(value?.to || '');
  }, [value?.from, value?.to]);

  const isDirty = (from || '') !== (value?.from || '') || (to || '') !== (value?.to || '');
  const hasActive = !!(value?.from || value?.to);

  const apply = () => {
    if (from && to && to < from) {
      // Swap on the fly so the range is always valid.
      onChange?.({ from: to, to: from });
    } else {
      onChange?.({ from, to });
    }
  };
  const clear = () => {
    setFrom(''); setTo('');
    onChange?.({ from: '', to: '' });
  };

  return (
    <div className={`inline-flex items-center gap-2 flex-wrap ${className}`} data-testid={testId}>
      <span className="inline-flex items-center gap-1 text-[10px] font-mono-edge uppercase tracking-[0.14em] text-muted-foreground">
        <Calendar size={12} /> Date
      </span>
      <input
        type="date"
        value={from}
        max={to || undefined}
        onChange={(e) => setFrom(e.target.value)}
        aria-label="From date"
        data-testid={`${testId}-from`}
        className="h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <span className="text-xs text-muted-foreground">to</span>
      <input
        type="date"
        value={to}
        min={from || undefined}
        onChange={(e) => setTo(e.target.value)}
        aria-label="To date"
        data-testid={`${testId}-to`}
        className="h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <Button
        size="sm"
        variant={isDirty ? 'default' : 'outline'}
        onClick={apply}
        disabled={!isDirty}
        data-testid={`${testId}-apply`}
        className="h-8 text-xs"
      >
        Apply
      </Button>
      {hasActive && (
        <Button
          size="sm"
          variant="ghost"
          onClick={clear}
          data-testid={`${testId}-clear`}
          className="h-8 text-xs px-2"
        >
          <X size={11} className="mr-1" /> Clear
        </Button>
      )}
    </div>
  );
}

export default AdminDateFilter;
